import type { ReactElement, ReactNode } from "react";
import { Fragment } from "react";

import { translate } from "app/filters/i18nFilter";
import type { TranslationReplacements } from "app/filters/i18nFilter";
import translateNested from "app/pages/sales-intelligence/common-components/i18n/translateNested";
import SignalSelectionTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalSelectionTooltip/SignalSelectionTooltip";
import type { SignalSelectionTooltipLabels } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalSelectionTooltipLabels";
import {
    getSignalDataTooltipLabels,
    getSignalTypeTooltipLabels,
} from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalSelectionTooltipLabels";
import { sanitizeSignalNameHtml } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";
import type { Translatable } from "app/pages/sales-intelligence/types";

type TooltipSlot = {
    readonly html: string;
    readonly labels: readonly string[];
};

const PARTIAL_SELECTION_HTML_PATTERN = /\+\d+/;
const SLOT_PREFIX = "\uE000";
const SLOT_SUFFIX = "\uE001";

const createSlotId = (index: number): string => `${SLOT_PREFIX}${index}${SLOT_SUFFIX}`;

const isTranslatable = (value: unknown): value is Translatable =>
    typeof value === "object" && value !== null && typeof (value as Translatable).key === "string";

const isHtmlPartialSelectionTranslatable = (translatable: Readonly<Translatable>): boolean => {
    const { key, replacements } = translatable;

    if (replacements && Object.keys(replacements).length > 0) {
        return false;
    }

    return key.includes("<b>") && PARTIAL_SELECTION_HTML_PATTERN.test(key);
};

const getTooltipLabelsForReplacement = (
    replacementKey: string,
    selectionTooltipLabels?: Readonly<SignalSelectionTooltipLabels>,
): readonly string[] | undefined => {
    if (
        replacementKey === "topics" ||
        replacementKey === "adNetworks" ||
        replacementKey === "name" ||
        replacementKey === "changeTypes"
    ) {
        return getSignalTypeTooltipLabels(selectionTooltipLabels);
    }

    if (replacementKey === "countries" || replacementKey === "technologies" || replacementKey === "personas") {
        return getSignalDataTooltipLabels(selectionTooltipLabels);
    }

    return undefined;
};

const resolveTranslatableWithSlots = (
    translatable: Readonly<Translatable>,
    selectionTooltipLabels: Readonly<SignalSelectionTooltipLabels> | undefined,
    slots: Map<string, TooltipSlot>,
    replacementKey?: string,
): string => {
    const tooltipLabels = replacementKey
        ? getTooltipLabelsForReplacement(replacementKey, selectionTooltipLabels)
        : getSignalTypeTooltipLabels(selectionTooltipLabels);

    if (isHtmlPartialSelectionTranslatable(translatable) && (tooltipLabels?.length ?? 0) > 1) {
        const slotId = createSlotId(slots.size);

        slots.set(slotId, { html: translatable.key, labels: tooltipLabels ?? [] });

        return slotId;
    }

    const { replacements } = translatable;

    if (!replacements || Object.keys(replacements).length === 0) {
        return translateNested(translatable);
    }

    const resolvedReplacements: TranslationReplacements = {};

    Object.entries(replacements).forEach(([key, value]) => {
        if (isTranslatable(value)) {
            resolvedReplacements[key] = resolveTranslatableWithSlots(value, selectionTooltipLabels, slots, key);
        } else {
            resolvedReplacements[key] = value;
        }
    });

    return translate(translatable.key, resolvedReplacements);
};

const renderTextWithTooltipSlots = (text: string, slots: ReadonlyMap<string, TooltipSlot>): ReactNode => {
    const slotId = [...slots.keys()].find((candidate) => text.includes(candidate));

    if (!slotId) {
        return (
            <span
                dangerouslySetInnerHTML={{
                    __html: sanitizeSignalNameHtml(text),
                }}
            />
        );
    }

    const slotIndex = text.indexOf(slotId);
    const slot = slots.get(slotId);

    if (!slot) {
        return text;
    }

    return (
        <>
            {renderTextWithTooltipSlots(text.slice(0, slotIndex), slots)}
            <SignalSelectionTooltip
                labels={slot.labels}
                inlineTextTrigger
                interaction="tooltip"
                triggerDataAutomation="signal-name-selection-tooltip-trigger"
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: sanitizeSignalNameHtml(slot.html),
                    }}
                />
            </SignalSelectionTooltip>
            {renderTextWithTooltipSlots(text.slice(slotIndex + slotId.length), slots)}
        </>
    );
};

type Props = {
    translatable: Readonly<Translatable>;
    selectionTooltipLabels?: SignalSelectionTooltipLabels;
};

const SignalNameHtmlWithSelectionTooltips = ({ translatable, selectionTooltipLabels }: Props): ReactElement => {
    const slots = new Map<string, TooltipSlot>();
    const resolvedText = resolveTranslatableWithSlots(translatable, selectionTooltipLabels, slots);

    if (slots.size === 0) {
        return (
            <span
                dangerouslySetInnerHTML={{
                    __html: sanitizeSignalNameHtml(resolvedText),
                }}
            />
        );
    }

    return <Fragment>{renderTextWithTooltipSlots(resolvedText, slots)}</Fragment>;
};

export default SignalNameHtmlWithSelectionTooltips;
